import React from "react";
import { Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import Navbar from "components/navbar";
import Sidebar from "components/sidebar";
import Footer from "components/footer/Footer";
import performanceKpiRoutes from "performanceKpiRoutes.js";
import { sessionManager } from "utils/sessionManager";
import { accessControl } from "utils/accessControl";
import { WorkersProvider } from "context/WorkersContext";
import { SectorPersonnelProvider } from "context/SectorPersonnelContext";
import { EvaluationsProvider } from "context/EvaluationsContext";
import { TrainingProvider } from "context/TrainingContext";
import { TrainingTopicsProvider } from "context/TrainingTopicsContext";
import { SectorKpiProvider } from "context/SectorKpiContext";
import toast, { Toaster } from "react-hot-toast";

/** Filter routes by role: non-admin users cannot see Workers, Sector Management & KPI, or Training topics */
function filterRoutesByRole(routes, isAdmin) {
  if (isAdmin) return routes;
  const adminOnlyNames = ["Workers", "Sector Management & KPI", "Training topics"];
  return routes
    .filter((r) => !adminOnlyNames.includes(r.name))
    .map((r) => {
      if (r.submenu) {
        const filteredSubmenu = r.submenu.filter((s) => !adminOnlyNames.includes(s.name));
        return { ...r, submenu: filteredSubmenu };
      }
      return r;
    })
    .filter((r) => !r.submenu || r.submenu.length > 0); // Hide parents with no visible children
}

export default function PerformanceKPI(props) {
  const { ...rest } = props;
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = React.useState(window.innerWidth >= 1200);
  const [currentRoute, setCurrentRoute] = React.useState("Performance KPI");

  const isAdmin = accessControl.isAdmin();
  const visibleRoutes = React.useMemo(
    () => filterRoutesByRole(performanceKpiRoutes, isAdmin),
    [isAdmin]
  );

  const isZoneDetails = false;

  React.useEffect(() => {
    if (!sessionManager.isSessionValid()) {
      toast.error('Session expired. Please login again.');
      navigate('/performance-kpi/login');
      return;
    }
    const cleanup = sessionManager.setupSessionCheck(() => {
      toast.error('Session expired due to inactivity. Please login again.');
      navigate('/performance-kpi/login');
    });
    return cleanup;
  }, [navigate, location.pathname]);

  React.useEffect(() => {
    const handleResize = () => setOpen(window.innerWidth >= 1200);
    window.addEventListener("resize", handleResize);
    handleResize();
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  React.useEffect(() => {
    updateActiveRoute();
  }, [location.pathname]);

  const updateActiveRoute = () => {
    const activeRouteName = findActiveRoute(visibleRoutes) || "Performance KPI";
    setCurrentRoute(activeRouteName);
    return activeRouteName;
  };

  const findActiveRoute = (routeList) => {
    for (let i = 0; i < routeList.length; i++) {
      const route = routeList[i];
      if (route.layout && route.path) {
        if (window.location.href.indexOf(route.layout + "/" + route.path) !== -1) {
          return route.name || null;
        }
      }
      if (route.submenu) {
        const childActive = findActiveRoute(route.submenu);
        if (childActive) return childActive;
      }
    }
    return null;
  };

  const getActiveNavbar = (routeList) => {
    for (let i = 0; i < routeList.length; i++) {
      const route = routeList[i];
      if (route.layout && route.path) {
        if (window.location.href.indexOf(route.layout + route.path) !== -1) {
          return route.secondary;
        }
      }
      if (route.submenu) {
        const childNavbar = getActiveNavbar(route.submenu);
        if (childNavbar) return childNavbar;
      }
    }
    return false;
  };

  const getRoutes = () => buildRoutes(visibleRoutes);

  const buildRoutes = (routeList) => {
    let collectedRoutes = [];
    routeList.forEach((route) => {
      if (route.path && route.component) {
        collectedRoutes.push(
          <Route path={route.path} element={route.component} key={route.path} />
        );
      }
      if (route.submenu) {
        collectedRoutes = collectedRoutes.concat(buildRoutes(route.submenu));
      }
    });
    return collectedRoutes;
  };

  document.documentElement.dir = "ltr";

  return (
    <div className="flex h-full w-full">
      <Toaster position="top-right" />
      <Sidebar open={open} onClose={() => setOpen(false)} routes={visibleRoutes} />
      <div className="h-full w-full bg-lightPrimary dark:!bg-navy-900">
        <main className="mx-[12px] h-full flex-none transition-all md:pr-2 xl:ml-[332px]">
          <div className="h-full">
            <Navbar
              onOpenSidenav={() => setOpen(true)}
              logoText={"Horizon UI Tailwind React"}
              brandText={currentRoute}
              secondary={getActiveNavbar(visibleRoutes)}
              hideRegistrationAndProfile
              {...rest}
            />
            <div className="pt-5s mx-auto mb-auto h-full min-h-[84vh] p-2 md:pr-2">
              <WorkersProvider>
                <SectorPersonnelProvider>
                  <EvaluationsProvider>
                    <SectorKpiProvider>
                      <TrainingTopicsProvider>
                        <TrainingProvider>
                        <Routes>
                          {getRoutes()}
                          <Route path="/" element={<Navigate to="/performance-kpi/dashboard" replace />} />
                          <Route path="*" element={<Navigate to="/performance-kpi/dashboard" replace />} />
                        </Routes>
                        </TrainingProvider>
                      </TrainingTopicsProvider>
                    </SectorKpiProvider>
                  </EvaluationsProvider>
                </SectorPersonnelProvider>
              </WorkersProvider>
            </div>
            <div className="p-3">
              <Footer />
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
